<template>
  <div class="max-w-[920px]">
    <FullScreenSpinner v-if="loading" />

    <RichText v-if="heading?.value" :field="heading" class="text-xl-bold mb-5" />

    <FormKit v-model="formData" type="form" @submit="onSubmit">
      <div class="md:flex">
        <RACQFormKit
          :bindings="{
            ...fuelTypeBindings,
            deselect: false,
          }"
        />
        <GoogleMapAddressInput
          :label="suburbLookup?.label?.value"
          :outerClass="suburbLookup?.outerClass?.value"
          :innerClass="suburbLookup?.innerClass?.value"
          wrapperClass=""
          :placeholder="suburbLookup?.placeholder?.value"
          :requiredValidationMessage="suburbLookup?.isRequiredErrorMessage?.value"
        />
        <FormKit type="submit" label="Search" inputClass="btn btn-md btn-primary mt-7" outerClass="md:ml-4" />
      </div>
    </FormKit>
    <hr class="my-5 md:my-6" />

    <RichText
      v-if="showInitialBanner"
      :field="initialSearchBanner"
      class="p-8 pr-12 grid md:flex gap-4 items-center bg-racq-anti-flash-white rounded"
    />
    <RichText
      v-else-if="!fuelStations.length"
      :field="resultNotFound"
      class="p-8 pr-12 gap-1 grid bg-racq-info-light-red rounded"
    />

    <!-- Fuel Stations Results -->
    <div id="fuel-stations-results">
      <SearchResultsList
        v-if="searchResultsListContent"
        :content="searchResultsListContent"
        :items="fuelStations"
        :itemsPerPage="itemsPerPage"
        :defaultPage="page"
        :lastUpdatedAt="lastUpdatedAt"
        scrollTarget="#fuel-stations-results"
        @pageChanged="updatePage"
      >
        <template #subHeader>
          <div v-if="fuelStations.length" class="md:flex grid gap-2 py-2">
            <RichText
              v-if="priceState == PriceIndicatorType.High"
              :field="priceHigh"
              class="px-6 py-5 gap-4 flex bg-racq-info-light-red rounded w-full"
            />
            <RichText
              v-else-if="priceState == PriceIndicatorType.Low"
              :field="priceLow"
              class="px-6 py-5 gap-4 flex bg-racq-info-light-green rounded w-full"
            />
            <RichText
              v-if="priceTrend == TrendIndicatorType.Up"
              :field="priceGoingUp"
              class="px-6 py-5 gap-4 flex bg-racq-anti-flash-white rounded w-full"
            />
            <RichText
              v-else-if="priceTrend == TrendIndicatorType.Down"
              :field="priceGoingDown"
              class="px-6 py-5 gap-4 flex bg-racq-anti-flash-white rounded w-full"
            />
          </div>
        </template>
        <template #item="{ item: station, index }">
          <!-- Fair Price Alert -->
          <div v-if="showPriceAlert(index)" class="flex items-center text-nowrap">
            <Tag
              v-tooltip.bottom="{
                content: fairFuelTooltip?.label?.value,
                triggers: [],
                shown: isTooltipOpen,
              }"
              outerClass="pr-8"
              :tagType="TagTypes.noIcon"
              :tagText="fairPriceLinkBreakLabel"
              :tagSize="TagSize.linkBreak"
              :tagBgColour="TagBgColour.error"
              @click="toggleTooltip"
            />
            <div class="border-b-2 w-full border-dashed !border-racq-error-red"></div>
          </div>
          <hr v-else-if="index" />
          <!-- Station Card -->
          <div class="flex pt-2 pb-1 md:pt-3 md:pb-2 md:my-2 items-center">
            <!-- Desktop Brand Logo -->
            <div class="mr-5 py-3 md:inline-block hidden">
              <div class="h-8 w-8 md:h-12 md:w-12 rounded content-center">
                <img class="mx-auto max-h-full max-w-full" :src="station.brandLogoSrc" :alt="station.brand" />
              </div>
            </div>
            <!-- Site Name -->
            <div class="py-3">
              <div class="mb-2 md:flex grid gap-2 md:gap-4 items-center">
                <div class="flex items-center">
                  <!-- Mobile Brand Logo -->
                  <div class="mr-2 py-3 md:hidden">
                    <div class="h-8 w-8 md:h-12 md:w-12 rounded content-center">
                      <img class="mx-auto max-h-full max-w-full" :src="station.brandLogoSrc" :alt="station.brand" />
                    </div>
                  </div>
                  <!-- Station Name -->
                  <div class="text-base-bold md:text-xl-bold text-racq-dark-grey mr-3 md:mr-0 line-clamp-2">
                    {{ station.name }}
                  </div>
                </div>
                <div class="flex gap-1 h-auto">
                  <Tag
                    v-if="station.discount && discountsLabel"
                    :tagType="TagTypes.discount"
                    :tagText="discountsLabel"
                    :tagSize="TagSize.sm"
                    :tagBgColour="TagBgColour.infoBlue"
                  />
                  <Tag
                    v-if="lowestPrice == station.price && cheapestLabel"
                    :tagType="TagTypes.noIcon"
                    :tagText="cheapestLabel"
                    :tagSize="TagSize.sm"
                    :tagBgColour="TagBgColour.antiFlashWhite"
                    :tagBorderColour="TagBorderColour.lightblue"
                  />
                </div>
              </div>
              <div class="flex">
                <div
                  class="align-text-top inline-block location-icon bg-center bg-no-repeat w-4 h-4 md:w-[18px] mdh-[18px] mr-1"
                ></div>
                <p class="text-xs md:text-m text-racq-med-grey line-clamp-1">
                  {{ station.address }}, {{ station.suburb }}
                </p>
              </div>
            </div>
            <!-- Price -->
            <div class="ml-auto w-[124px] md:w-[152px]">
              <div
                class="text-xl2-bold md:text-xl3-bold w-[124px] md:w-[152px] h-[78px] md:h-[60px] content-center text-center text-racq-dark-grey bg-racq-neutral-grey rounded"
              >
                {{ station.price.toFixed(1) }}
              </div>
              <div class="mt-1 text-xs text-racq-med-grey text-center">
                <RichText :field="computedLastUpdatedAt(station.lastUpdated, station.dataSourceName)" />
              </div>
            </div>
          </div>
          <hr v-if="index + 1 == paginatedStations.length" />
        </template>
      </SearchResultsList>
      <RichText :field="fuelStations?.length ? copyrightWithResults : copyright" />
    </div>
  </div>
</template>

<script setup lang="ts">
import FullScreenSpinner from '@/components/FullScreenSpinner/FullScreenSpinner.vue';
import GoogleMapAddressInput from '@/components/GoogleMap/GoogleMapAddressInput.vue';
import RACQFormKit from '@/components/RACQFormKit/RACQFormKit.vue';
import Tag from '@/components/Tag/Tag.vue';
import SearchResultsList from '@/components/SearchResultsList/SearchResultsList.vue';
import { convertSitecoreFormFieldToVueFormField } from '@/composables/useFormInputData';
import { TagBgColour, TagBorderColour, TagSize, TagTypes } from '@/enums/tag-enums';
import { ISitecoreFormGenericInput } from '@/interfaces';
import { useGoogleMapStore } from '@/store';
import { useFuelFinderStore } from '@/store/fuel-finder/fuel-finder.state';
import { format, formatDistanceToNow } from 'date-fns';
import { computed, onMounted, ref } from 'vue';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { PriceIndicatorType, TrendIndicatorType } from '@/enums/fuel-price-trend.enum';
import { PageAnalyticsBuilder, PageEvents, SiteSection } from '@/utils/page-analytics-builder';
import { useRoute } from 'vue-router';

const props = defineProps<{
  content: ISitecoreFormGenericInput[];
  id: string;
  name: string;
}>();
const itemsPerPage = 10;
const searchResultsListContent = props.content.find((item) => item.name == 'searchResultsList') as any;
const fairFuelTooltip = props.content.find((item) => item.name == 'fairFuelTooltip') as any;
const suburbLookup = props.content.find((item) => item.name == 'suburbLookup') as any;

const googleMapStore = useGoogleMapStore();
googleMapStore.setSuburbPostcodeMode(true);
const fuelFinderStore = useFuelFinderStore();
const route = useRoute();

const formData = ref<Record<string, string>>({});
const loading = ref(false);
const page = ref(1);
const showInitialBanner = ref(true);
const isTooltipOpen = ref(false);

const fuelTypeBindings = computed(() => {
  const input = props.content.find((input) => input.name == 'fuelType');
  return convertSitecoreFormFieldToVueFormField(input!, 'fuelType');
});

const fuelStations = computed(() => fuelFinderStore.fuelStations);
const lowestPrice = computed(() => fuelFinderStore.lowestPrice);
const averagePrice = computed(() => fuelFinderStore.averagePrice);
const priceState = computed(() => fuelFinderStore.priceState);
const priceTrend = computed(() => fuelFinderStore.priceTrend);
const lastUpdatedAt = computed(() => format(fuelFinderStore.lastUpdated ?? new Date(), 'dd MMM HH:mm a'));
const paginatedStations = computed(() =>
  fuelStations.value.slice((page.value - 1) * itemsPerPage, page.value * itemsPerPage)
);

// -----------------------------------
// Compute labels
// -----------------------------------
const computedLastUpdatedAt = (lastUpdatedAt: string, dataSource: string) =>
  computed(() => {
    const dataSourceSymbol = dataSource?.includes('OPIS') ? '^' : '*';
    return replaceBookmark(
      'lastUpdated',
      (props.content.find((item) => item.name == 'priceChangeLabel')?.label?.value ?? '') + dataSourceSymbol,
      fromNow(lastUpdatedAt)
    );
  });

const fairPriceLinkBreakLabel = computed(() => {
  return replaceBookmark(
    'fairFuelPrice',
    props.content.find((item) => item.name == 'fairFuelLineBreakLabel')?.label?.value ?? '',
    fuelFinderStore.averagePrice?.toFixed(2)
  );
});

const initialSearchBanner = computed(() => props.content.find((item) => item.name == 'initialSearchBanner')?.label);
const cheapestLabel = computed(() => props.content.find((item) => item.name == 'cheapestLabel')?.label?.value);
const discountsLabel = computed(() => props.content.find((item) => item.name == 'discountLabel')?.label?.value);
const heading = computed(() => props.content.find((item) => item.name == 'heading')?.label);
const resultNotFound = computed(() => props.content.find((item) => item.name == 'resultNotFound')?.label);
const priceHigh = computed(() => props.content.find((item) => item.name == 'priceHigh')?.label);
const priceLow = computed(() => props.content.find((item) => item.name == 'priceLow')?.label);
const priceGoingUp = computed(() => props.content.find((item) => item.name == 'priceGoingUp')?.label);
const priceGoingDown = computed(() => props.content.find((item) => item.name == 'priceGoingDown')?.label);
const copyright = computed(() => props.content.find((item) => item.name == 'copyright')?.label);
const copyrightWithResults = computed(() => props.content.find((item) => item.name == 'copyrightWithResults')?.label);

onMounted(async () => {
  await googleMapStore.getSCGoogleMapsSettings();
  if (googleMapStore.googleMapSettingsItem) {
    googleMapStore.setApiKey(googleMapStore.googleMapSettingsItem.apiKey);
    await googleMapStore.loadGoogleMapsApi();
  }
  pushAnalytics();
});

const onSubmit = async () => {
  loading.value = true;
  showInitialBanner.value = false;
  await fuelFinderStore
    .findFuel(googleMapStore.latitude, googleMapStore.longitude, formData.value.fuelType)
    .finally(() => {
      page.value = 1;
      loading.value = false;
    });

  pushAnalytics(true);
};

const fromNow = (timestring: string) => {
  return formatDistanceToNow(new Date(timestring));
};

const showPriceAlert = (index: number) => {
  if (Number.isNaN(averagePrice.value) || !Number(averagePrice.value)) return false;
  const totalIndex = index + (page.value - 1) * itemsPerPage;
  const currentStation = fuelStations?.value[totalIndex];
  const previousStation = totalIndex ? fuelStations?.value[totalIndex - 1] : null;

  if (!previousStation && currentStation?.price > averagePrice.value) return true; // edge case if lowest price is higher than fair price

  return previousStation && previousStation.price <= averagePrice.value && currentStation?.price > averagePrice.value;
};

const updatePage = (p: number) => {
  page.value = p;
  scrollToTop();
};

const scrollToTop = () => {
  const iframe = window.parent.document.querySelector(
    (route.query.iframeSelector as string) ?? 'iframe'
  ) as HTMLIFrameElement;
  iframe?.scrollIntoView({
    block: 'start',
    inline: 'start',
    behavior: 'smooth',
  });
};

const pushAnalytics = async (searchInfo?: boolean) => {
  // Push analytics
  const pageAnalytics = new PageAnalyticsBuilder();
  pageAnalytics.pageAnalytics.webPageDetails = {
    name: 'Fuel prices',
    siteSection: 'RACQ - Car',
  };
  pageAnalytics.pageAnalytics.form = {
    authStatus: 'Not applicable',
  };
  delete pageAnalytics.pageAnalytics.profileInfo;
  pageAnalytics.pageAnalytics.event = `${SiteSection.RACQ}.${PageEvents.PAGE_VIEW}`;

  // extra info for search
  if (searchInfo) {
    pageAnalytics.pageAnalytics.searchInfo = {
      onsiteSearchFilter: formData.value.fuelType,
      onsiteSearchTerm: googleMapStore.formattedAddress,
      onsiteSearchType: 'Fuel prices',
    };
    const onsiteSearchResults = [];
    if ([PriceIndicatorType.High, PriceIndicatorType.Low].includes(priceState.value!)) {
      onsiteSearchResults.push(
        priceState.value == PriceIndicatorType.High ? priceHigh.value?.value : priceLow.value?.value
      );
    }
    if (priceTrend.value) {
      onsiteSearchResults.push(
        TrendIndicatorType.Up == priceTrend.value ? priceGoingUp.value?.value : priceGoingDown.value?.value
      );
    }

    pageAnalytics.pageAnalytics.searchInfo.onsiteSearchResults = onsiteSearchResults
      .join(' - ')
      .replace(/<[^>]*>/g, '')
      .replaceAll('\n', '');
    pageAnalytics.pageAnalytics.event = `${SiteSection.RACQ}.${PageEvents.PAGE_SEARCH}`;
  }
  pageAnalytics.pushAnalytics();
};

const toggleTooltip = () => {
  isTooltipOpen.value = !isTooltipOpen.value;
  if (isTooltipOpen.value) {
    setTimeout(() => {
      document.querySelector('.v-popper__inner')?.addEventListener('click', () => {
        isTooltipOpen.value = false;
      });
    }, 500);
  }
};
</script>
